import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Trash2,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { vendorsService } from '@/services/vendorsService'
import { Vendor } from '@/api/types'

export const VendorsList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingVendorId, setDeletingVendorId] = useState<number | null>(null)

  const branchId = user?.selectedBranch?.branchId

  // Load vendors
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadVendors = async () => {
      try {
        setLoading(true)
        const response = await vendorsService.getAllVendors(branchId, pageNumber, pageSize)
        const list = response.data || []
        setVendors(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('vendors.failedToLoadVendors'))
      } finally {
        setLoading(false)
      }
    }

    loadVendors()
  }, [branchId, pageNumber, pageSize])

  const filteredVendors = useMemo(() => {
    if (!searchQuery.trim()) return vendors
    const query = searchQuery.toLowerCase()
    return vendors.filter((vendor) => {
      return (
        (vendor.vendorNameEn || '').toLowerCase().includes(query) ||
        (vendor.vendorNameAr || '').toLowerCase().includes(query) ||
        (vendor.email || '').toLowerCase().includes(query) ||
        (vendor.mobileNo || vendor.phoneNo || '').toLowerCase().includes(query) ||
        String(vendor.vendorId || vendor.id || '').includes(query)
      )
    })
  }, [vendors, searchQuery])

  const handleAddNew = () => {
    navigate('/purchases/vendors/new')
  }

  const handleEdit = (vendor: Vendor) => {
    const vendorId = vendor.vendorId ?? vendor.id
    if (!vendorId) {
      toast.error(t('vendors.invalidVendorId'))
      return
    }
    navigate(`/purchases/vendors/${vendorId}/edit`)
  }

  // Get user IP address (logged-in user's IP, not server's IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleDelete = async (vendor: Vendor) => {
    const vendorId = vendor.vendorId ?? vendor.id
    if (!vendorId || !branchId) {
      toast.error(t('vendors.invalidVendorId'))
      return
    }

    if (!confirm(t('vendors.confirmDelete', { name: vendor.vendorNameEn || vendor.vendorNameAr || t('vendors.thisVendor') }))) {
      return
    }

    try {
      setDeletingVendorId(vendorId)
      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()
      await vendorsService.deleteVendor(vendorId, ipAddress)
      toast.success(t('vendors.vendorDeletedSuccess'))
      // Reload vendors
      const response = await vendorsService.getAllVendors(branchId, pageNumber, pageSize)
      setVendors(response.data || [])
      setTotalCount(response.totalRecords || 0)
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('vendors.failedToDeleteVendor'))
    } finally {
      setDeletingVendorId(null)
    }
  }

  const handlePrintList = () => {
    window.print()
  }

  const handleCopy = () => {
    const tableData = filteredVendors.map(v => ({
      Id: v.vendorId || v.id,
      Name: v.vendorNameEn || '',
      'Name Arabic': v.vendorNameAr || '-',
      'Mobile No': v.mobileNo || '-',
      Email: v.email || '-',
      'Active Status': v.activeStatus ? 'Active' : 'Inactive',
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    navigator.clipboard.writeText(csv)
    toast.success(t('vendors.copiedToClipboard'))
  }

  const handleExportCSV = () => {
    const tableData = filteredVendors.map(v => ({
      Id: v.vendorId || v.id,
      Name: v.vendorNameEn || '',
      'Name Arabic': v.vendorNameAr || '-',
      'Mobile No': v.mobileNo || '-',
      Email: v.email || '-',
      'Active Status': v.activeStatus ? 'Active' : 'Inactive',
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `vendors_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('vendors.exportedToCsv'))
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1) // Reset to first page when page size changes
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('vendors.title')}
        description={t('vendors.manageVendors')}
        action={{
          label: t('vendors.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrintList}>
            <Printer className="h-4 w-4 mr-2" />
            {t('common.print')}
          </Button>
        </div>
      </PageHeader>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">{t('vendors.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('vendors.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('vendors.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('vendors.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">{t('vendors.loadingVendors')}</div>
            </div>
          ) : filteredVendors.length === 0 ? (
            <EmptyState
              title={t('vendors.noVendorsFound')}
              description={t('vendors.getStarted')}
              action={{
                label: t('vendors.addNewVendorAction'),
                onClick: handleAddNew,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={`w-[80px] ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('vendors.id')}</TableHead>
                      <TableHead className={rtl ? 'text-right pr-6' : 'text-left pl-6'}>{t('vendors.vendorName')}</TableHead>
                      <TableHead className={rtl ? 'text-right pr-6' : 'text-left pl-6'}>{t('vendors.nameArabic')}</TableHead>
                      <TableHead className={rtl ? 'text-right pr-6' : 'text-left pl-6'}>{t('vendors.mobileNo')}</TableHead>
                      <TableHead className={rtl ? 'text-right pr-6' : 'text-left pl-6'}>{t('vendors.email')}</TableHead>
                      <TableHead className={rtl ? 'text-right pr-6' : 'text-left pl-6'}>{t('vendors.status')}</TableHead>
                      <TableHead className={`w-[100px] ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('common.edit')}</TableHead>
                      <TableHead className={`w-[100px] ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('common.delete')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVendors.map((vendor) => {
                      const vendorId = vendor.vendorId ?? vendor.id
                      return (
                        <TableRow key={vendorId}>
                          <TableCell className={rtl ? 'text-right' : 'text-left'}>{vendorId}</TableCell>
                          <TableCell className={`font-medium ${rtl ? 'text-right' : 'text-left'}`}>
                            {vendor.vendorNameEn || '-'}
                          </TableCell>
                          <TableCell className={rtl ? 'text-right' : 'text-left'}>{vendor.vendorNameAr || '-'}</TableCell>
                          <TableCell className={rtl ? 'text-right' : 'text-left'}>{vendor.mobileNo || vendor.phoneNo || '-'}</TableCell>
                          <TableCell className={rtl ? 'text-right' : 'text-left'}>{vendor.email || '-'}</TableCell>
                          <TableCell className={rtl ? 'text-right' : 'text-left'}>
                            {vendor.activeStatus ? (
                              <Badge variant="default">
                                <Check className="w-3 h-3 mr-1" />
                                {t('common.active')}
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <X className="w-3 h-3 mr-1" />
                                {t('common.inactive')}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(vendor)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(vendor)}
                              disabled={deletingVendorId === vendorId}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  {t('vendors.showing')} {((pageNumber - 1) * pageSize) + 1} {t('vendors.to')} {Math.min(pageNumber * pageSize, totalCount)} {t('vendors.of')} {totalCount} {t('vendors.entries')}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.pageSize')}</span>
                    <select
                      className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={String(pageSize)}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('vendors.previous')}
                  </Button>
                  <div className="text-sm">
                    {t('vendors.page')} {pageNumber} {t('vendors.of')} {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    {t('vendors.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

